// Service autonome de la case acheteur Operation MS. Routes acheteur /subsequents/*.
// Aucun sniffing d'URL : l'operationId est passe en argument.
import { OperationMS, AnalyseIaStatut } from '../types';

const authedFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json().catch(() => ({})) as Promise<T>;
};

// Charge l'operation MS : GET /subsequents/operation/:id (la reponse peut etre {data} ou l'objet).
export const getOperationMs = async (operationId: number | string): Promise<OperationMS> => {
  const json = await authedFetch<OperationMS & { data?: OperationMS }>(
    `/subsequents/operation/${operationId}`,
  );
  return json.data ?? json;
};

export interface UnreadCountsApi {
  questions_reponses: number;
  messagerie: number;
}

// Compteurs de non-lus par canal : GET /subsequents/messages/unread-counts (acheteur).
export const getUnreadCounts = async (
  operationId: number | string,
): Promise<UnreadCountsApi | null> => {
  const json = await authedFetch<{ data?: Record<string, UnreadCountsApi> }>(
    `/subsequents/messages/unread-counts?operation_ids[]=${operationId}&auteur_type=acheteur`,
  );
  return json.data?.[String(operationId)] ?? null;
};

// Nombre de messages du canal Questions/reponses : GET /subsequents/operation/:id/messagerie.
export const getQuestionsReponsesCount = async (operationId: number | string): Promise<number> => {
  const json = await authedFetch<{ data?: unknown[] }>(
    `/subsequents/operation/${operationId}/messagerie?channel=questions_reponses`,
  );
  return Array.isArray(json.data) ? json.data.length : 0;
};

// Erreur de validation 422 portant les erreurs par champ (pour remapper vers le formulaire).
export class ConsultationValidationError extends Error {
  validationErrors: Record<string, string[]>;

  constructor(validationErrors: Record<string, string[]>) {
    super('Erreurs de validation détectées');
    this.name = 'ConsultationValidationError';
    this.validationErrors = validationErrors;
  }
}

export interface SaveDescriptifResponse {
  status?: string;
  message?: string;
  data?: unknown;
}

// Sauvegarde le descriptif : PUT /subsequents/operation/:id. Remonte les 422 via
// ConsultationValidationError pour le mapping de champs.
export const saveOperationDescriptif = async (
  operationId: number | string,
  body: Record<string, unknown>,
): Promise<SaveDescriptifResponse> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${operationId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      message?: string;
      errors?: Record<string, string[]>;
    };
    if (response.status === 422 && errorData.errors) {
      throw new ConsultationValidationError(errorData.errors);
    }
    throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
  }
  return (await response.json().catch(() => ({}))) as SaveDescriptifResponse;
};

// Lance la consultation : PUT /subsequents/:id/consultation (passe l'operation a status 2,
// les commandes a 3). Le payload est construit par domain/operationConsultation.
export const lancerConsultation = (
  operationId: number | string,
  payload: Record<string, unknown>,
): Promise<unknown> =>
  authedFetch(`/subsequents/${operationId}/consultation`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

// Annule l'operation (et ses commandes) : POST /subsequents/operation/:id/annuler -> statut 14.
export const annulerOperation = (operationId: number | string): Promise<unknown> =>
  authedFetch(`/subsequents/operation/${operationId}/annuler`, { method: 'POST' });

// Prolongation du delai de reception des offres (apres lancement) : POST /subsequents/operation/:id/prolongation.
// Met a jour expire_at + date_limite_remise ; le core journalise la demande (prefix "Prolongation").
export const prolongerDelai = (
  operationId: number | string,
  nouvelleDateLimite: string,
): Promise<unknown> =>
  authedFetch(`/subsequents/operation/${operationId}/prolongation`, {
    method: 'POST',
    body: JSON.stringify({ nouvelle_date_limite: nouvelleDateLimite }),
  });

// --- Analyse acheteur ----------------------------------------------------------

export interface DechiffrageResponse {
  data?: { created_at?: string; operateur?: string | null };
}

// Dechiffre / ouvre les plis : POST /subsequents/operation/:id/dechiffrer.
export const dechiffrerPlis = (operationId: number | string): Promise<DechiffrageResponse> =>
  authedFetch<DechiffrageResponse>(`/subsequents/operation/${operationId}/dechiffrer`, {
    method: 'POST',
  });

// --- Analyse IA des offres (asynchrone : dispatch + polling) --------------------

export interface AnalyseIaStatutResponse {
  data?: {
    statut?: AnalyseIaStatut;
    at?: string | null;
    erreurs?: Array<{ commande_id?: number; code?: string }> | string[] | null;
  };
}

// Lance l'analyse IA des offres : POST /subsequents/operation/:id/analyse-ia (202, async).
// `force` relance malgre la garde d'idempotence (bouton "Relancer").
export const lancerAnalyseIa = (
  operationId: number | string,
  force = false,
): Promise<AnalyseIaStatutResponse> =>
  authedFetch<AnalyseIaStatutResponse>(
    `/subsequents/operation/${operationId}/analyse-ia${force ? '?force=1' : ''}`,
    { method: 'POST' },
  );

// Statut de l'analyse IA (polling front) : GET /subsequents/operation/:id/analyse-ia/statut.
export const getAnalyseIaStatut = (
  operationId: number | string,
): Promise<AnalyseIaStatutResponse> =>
  authedFetch<AnalyseIaStatutResponse>(`/subsequents/operation/${operationId}/analyse-ia/statut`);

// Enregistre les notes techniques + le retenu : PUT /subsequents/operation/:id/note.
export const saveNotesTechniques = (
  operationId: number | string,
  body: object,
): Promise<unknown> =>
  authedFetch(`/subsequents/operation/${operationId}/note`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export interface FinaliserAnalyseResponse {
  id_commande?: number;
  message?: string;
}

// Cloture l'analyse (notes + retenu + signataire) : POST /subsequents/operation/:id/validate.
// Renvoie l'id de la commande retenue (data.id_commande) pour synchroniser l'etat local.
export const finaliserAnalyse = (
  operationId: number | string,
  body: object,
): Promise<FinaliserAnalyseResponse> =>
  authedFetch<FinaliserAnalyseResponse>(`/subsequents/operation/${operationId}/validate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export interface SignataireApi {
  id: number;
  prenom?: string;
  nom?: string;
  type_signataire?: string;
  email_contact?: string;
}
export interface SignatairesResponse {
  status?: string;
  data?: SignataireApi[];
}

// Liste les signataires d'un organisme : GET /organismes/:id/signataires.
export const getSignataires = (organismeId: number | string): Promise<SignatairesResponse> =>
  authedFetch<SignatairesResponse>(`/organismes/${organismeId}/signataires`, { method: 'GET' });

// --- Descriptif financier : sections / articles (DPGF) -------------------------
// CRUD du DPGF acheteur MS. Routes /subsequents/operation/:id/section|article.
// Tout est id-based (operationId en argument) : aucun sniffing d'URL.

export interface SectionApiData {
  id?: number | string;
  nom?: string;
}
export interface SectionApiResponse {
  data?: SectionApiData;
}

export interface ArticleApiData {
  id?: number | string;
  nom?: string;
  code?: number;
}
export interface ArticleApiResponse {
  data?: ArticleApiData;
}

// Cree une section (chapitre) : POST /subsequents/operation/:id/section.
export const createSection = (
  operationId: number | string,
  name: string,
): Promise<SectionApiResponse> =>
  authedFetch<SectionApiResponse>(`/subsequents/operation/${operationId}/section`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

// Supprime une section : DELETE /subsequents/operation/:id/section/:sectionId.
export const deleteSection = (
  operationId: number | string,
  sectionId: number | string,
): Promise<unknown> =>
  authedFetch(`/subsequents/operation/${operationId}/section/${sectionId}`, { method: 'DELETE' });

// Cree un article (ligne DPGF) : POST /subsequents/operation/:id/article.
export const createArticle = (
  operationId: number | string,
  payload: { nom: string; code: number; id_section: number | string },
): Promise<ArticleApiResponse> =>
  authedFetch<ArticleApiResponse>(`/subsequents/operation/${operationId}/article`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// Supprime un article : DELETE /subsequents/operation/:id/article/:articleId.
export const deleteArticle = (
  operationId: number | string,
  articleId: number | string,
): Promise<unknown> =>
  authedFetch(`/subsequents/operation/${operationId}/article/${articleId}`, { method: 'DELETE' });
